import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, } from 'react';
import { cx } from '../../../internal/cx.js';
import { formatNumber } from '../../../internal/format-number.js';
import { InfoGlyph, WarningGlyph } from '../../../internal/icons.js';
import { IconButton } from '../../actions/button/index.js';
import { Money } from '../../billing/money/index.js';
import { DeltaIndicator } from '../../charts/delta-indicator/index.js';
import { InlineErrorRetry } from '../../feedback/inline-error-retry/index.js';
import { Skeleton } from '../../feedback/skeleton/index.js';
import { VisuallyHidden } from '../../layout/visually-hidden/index.js';
import { Popover, PopoverContent, PopoverTrigger } from '../../overlays/popover/index.js';
function isEmpty(value) {
    return value === null || value === undefined;
}
function FormattedValue({ value, format, currency, locale, notation, formatOptions, }) {
    if (format === 'currency' && (typeof value === 'number' || typeof value === 'string')) {
        return (_jsx(Money, { amount: value, currency: currency ?? '', ...(locale !== undefined && { locale }), ...(notation !== undefined && { notation }), ...(formatOptions?.maximumFractionDigits !== undefined && {
                maximumFractionDigits: formatOptions.maximumFractionDigits,
            }), ...(formatOptions?.minimumFractionDigits !== undefined && {
                minimumFractionDigits: formatOptions.minimumFractionDigits,
            }) }));
    }
    if ((format === 'number' || format === 'percent') && typeof value === 'number') {
        const opts = {
            kind: format,
            ...(locale !== undefined && { locale }),
            ...(formatOptions !== undefined && { options: formatOptions }),
        };
        const shown = formatNumber(value, { ...opts, notation: notation ?? 'standard' });
        if (notation === 'compact') {
            const full = formatNumber(value, opts);
            if (full !== shown) {
                return (_jsxs("span", { title: full, children: [_jsx("span", { "aria-hidden": true, children: shown }), _jsx(VisuallyHidden, { children: full })] }));
            }
        }
        return _jsx(_Fragment, { children: shown });
    }
    return _jsx(_Fragment, { children: value });
}
/**
 * A single KPI for dashboards: label, value, change, comparison period and
 * optional hint, help, sparkline and drill-down link. Group several with
 * StatCardGroup.
 */
export const StatCard = forwardRef(function StatCard({ label, value, delta, deltaDirection = 'flat', deltaTone, caption, visual, change, format, currency, locale, notation, formatOptions, unit, hint, help, helpLabel, icon, sparkline, tone, size, variant, href, linkComponent: LinkComponent = 'a', loading = false, emptyLabel = 'No data', error, onRetry, semantics = 'text', className, ...rest }, ref) {
    const dl = semantics === 'description-list';
    const Body = dl ? 'dl' : 'div';
    const Term = dl ? 'dt' : 'p';
    const hasError = !loading && error !== undefined && error !== null && error !== false;
    const blockValue = loading || (hasError && onRetry !== undefined);
    const Line = dl ? 'dd' : 'p';
    const ValueEl = dl ? 'dd' : blockValue ? 'div' : 'p';
    const SlotEl = dl ? 'dd' : 'div';
    const legacyTone = deltaTone ??
        (deltaDirection === 'up' ? 'success' : deltaDirection === 'down' ? 'danger' : 'neutral');
    const srDirection = deltaDirection === 'up' ? 'increase' : deltaDirection === 'down' ? 'decrease' : 'no change';
    const resolvedHelpLabel = helpLabel ??
        (typeof label === 'string' || typeof label === 'number'
            ? `About ${label}`
            : 'About this metric');
    const labelContent = href ? (_jsx(LinkComponent, { className: "mn-stat__link", href: href, children: label })) : (label);
    let valueContent;
    if (loading) {
        valueContent = _jsx(Skeleton, { shape: "text", width: "5ch", className: "mn-stat__skeleton" });
    }
    else if (hasError) {
        valueContent = onRetry ? (_jsx(InlineErrorRetry, { onRetry: onRetry, children: error })) : (_jsxs("span", { className: "mn-stat__error", children: [_jsx(WarningGlyph, {}), error] }));
    }
    else if (isEmpty(value)) {
        valueContent = (_jsxs(_Fragment, { children: [_jsx("span", { "aria-hidden": true, children: "\u2014" }), _jsx(VisuallyHidden, { children: emptyLabel })] }));
    }
    else {
        valueContent = (_jsxs(_Fragment, { children: [_jsx(FormattedValue, { value: value, format: format, currency: currency, locale: locale, notation: notation, formatOptions: formatOptions }), unit !== undefined && unit !== null ? _jsx("span", { className: "mn-stat__unit", children: unit }) : null] }));
    }
    const showFooter = !loading && !hasError;
    let footer = null;
    if (showFooter && change) {
        footer = (_jsx(Line, { className: "mn-stat__footer", children: _jsx(DeltaIndicator, { size: size === 'lg' ? 'md' : 'sm', ...change, comparison: change.comparison ?? caption, className: cx('mn-stat__change', change.className) }) }));
    }
    else if (showFooter && (delta !== undefined || caption)) {
        footer = (_jsxs(Line, { className: "mn-stat__footer", children: [delta !== undefined ? (_jsxs("span", { className: "mn-stat__delta", "data-tone": legacyTone, "data-direction": deltaDirection, children: [_jsx("span", { "aria-hidden": true, children: deltaDirection === 'up' ? '↑' : deltaDirection === 'down' ? '↓' : '→' }), _jsxs(VisuallyHidden, { children: [srDirection, " "] }), delta] })) : null, caption ? _jsx("span", { className: "mn-stat__caption", children: caption }) : null] }));
    }
    const state = loading ? 'loading' : hasError ? 'error' : isEmpty(value) ? 'empty' : undefined;
    return (_jsxs("div", { ref: ref, ...rest, className: cx('mn-stat', className), "data-size": size, "data-variant": variant, "data-tone": tone, "data-state": state, "data-linked": href ? '' : undefined, "aria-busy": loading || rest['aria-busy'] || undefined, children: [_jsxs(Body, { className: "mn-stat__body", children: [_jsxs(Term, { className: "mn-stat__label", children: [icon ? (_jsx("span", { className: "mn-stat__icon", "aria-hidden": true, children: icon })) : null, icon || help ? (_jsx("span", { className: "mn-stat__label-text", children: labelContent })) : (labelContent), help ? (_jsxs(Popover, { children: [_jsx(PopoverTrigger, { asChild: true, children: _jsx(IconButton, { className: "mn-stat__help", size: "xs", label: resolvedHelpLabel, icon: _jsx(InfoGlyph, {}) }) }), _jsx(PopoverContent, { className: "mn-stat__help-content", children: help })] })) : null] }), _jsx(ValueEl, { className: "mn-stat__value", children: valueContent }), footer, hint && !loading && !hasError ? _jsx(Line, { className: "mn-stat__hint", children: hint }) : null, sparkline && !loading && !hasError ? (_jsx(SlotEl, { className: "mn-stat__sparkline", "aria-hidden": true, children: sparkline })) : null] }), visual ? (_jsx("div", { className: "mn-stat__visual", "aria-hidden": true, children: visual })) : null] }));
});
/** A responsive grid of StatCards (a KPI row). */
export const StatCardGroup = forwardRef(function StatCardGroup({ label, columns, minItemWidth, className, style, ...rest }, ref) {
    const vars = {};
    if (columns !== undefined) {
        const n = Math.max(1, Math.floor(columns));
        vars['--mn-stat-group-columns'] = n;
        vars['--mn-stat-group-columns-narrow'] = Math.min(2, n);
    }
    if (minItemWidth !== undefined)
        vars['--mn-stat-group-min'] = minItemWidth;
    return (_jsx("div", { ref: ref, role: label ? 'group' : undefined, "aria-label": label, ...rest, className: cx('mn-stat-group', className), "data-columns": columns !== undefined ? '' : undefined, style: { ...vars, ...style } }));
});
//# sourceMappingURL=stat-card.js.map